import React from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';

const InventoryList = ({ inventory, loading, error, viewMode, onEdit, onDelete }) => {
  // Ensure inventory is always an array
  const items = Array.isArray(inventory) ? inventory : [];

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading inventory items...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()} className="btn btn-sm btn-secondary">Retry</button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="no-data-state">
        <div className="no-data-icon">📦</div>
        <h3>No Inventory Items</h3>
        <p>Get started by adding your first product.</p>
      </div>
    );
  }

  // Dynamic column configuration
  const columns = [
    { id: 'sku', label: 'Item Code' },
    { id: 'name', label: 'Product Name' },
    { id: 'category', label: 'Category' },
    { id: 'hsn', label: 'HSN' },
    { id: 'gst', label: 'GST (%)' },
    { id: 'cost', label: 'Cost Price' },
    { id: 'price', label: 'Selling Price' },
    { id: 'stock', label: 'Stock' }
  ];

  const renderFieldValue = (item, colId, quantity, reorderLevel, costPrice, sellingPrice) => {
    switch (colId) {
      case 'sku':
        return <span className="fw-medium text-secondary">{item.public_id || item.sku || 'N/A'}</span>;
      case 'name':
        return <span className="fw-semibold text-dark">{item.name}</span>;
      case 'category':
        return item.category?.name || item.category_name || 'Uncategorized';
      case 'hsn':
        return item.hsn || 'N/A';
      case 'gst':
        return `${item.gst_rate || 0}%`;
      case 'cost':
        return `₹${costPrice.toFixed(2)}`;
      case 'price':
        return <span className="text-primary fw-semibold">₹{sellingPrice.toFixed(2)}</span>;
      case 'stock':
        return (
          <span className={`badge ${quantity <= reorderLevel ? 'bg-warning text-dark' : 'bg-info text-dark'}`}>
            {quantity}
          </span>
        );
      default:
        return null;
    }
  };

  if (viewMode === 'grid') {
    return (
      <div className="inventory-grid">
        {items.map(item => {
          const quantity = parseFloat(item.quantity) || 0;
          const reorderLevel = parseFloat(item.reorder_level) || 0;
          const costPrice = parseFloat(item.cost_price) || 0;
          const sellingPrice = parseFloat(item.selling_price) || 0;

          return (
            <div key={item.id} className="inventory-card">
              <div className="card-header">
                <h3>{item.name}</h3>
                <span className={`status ${item.status}`}>
                  {item.status ? item.status.replace('_', ' ') : 'N/A'}
                </span>
              </div>
              <div className="card-body">
                {columns.map(col => (
                  <div key={col.id} className="d-flex justify-content-between mb-2 pb-1 border-bottom border-light">
                    <span className="text-muted small">{col.label}:</span>
                    <span className="small text-end">
                      {renderFieldValue(item, col.id, quantity, reorderLevel, costPrice, sellingPrice)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="card-actions">
                <button onClick={() => onEdit(item)} className="btn btn-sm btn-secondary">
                  <FiEdit2 /> Edit
                </button>
                <button onClick={() => onDelete(item.id)} className="btn btn-sm btn-danger">
                  <FiTrash2 /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Table-specific columns that include serial and actions
  const tableColumns = [
    { id: 'serial', label: '#' },
    ...columns,
    { id: 'status', label: 'Status' },
    { id: 'actions', label: 'Actions' }
  ];

  return (
    <div className="inventory-table-container table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }}>
      <table className="table table-hover w-100">
        <thead>
          <tr>
            {tableColumns.map(col => (
              <th key={col.id} className={col.id === 'actions' ? 'text-end' : ''}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const quantity = parseFloat(item.quantity) || 0;
            const reorderLevel = parseFloat(item.reorder_level) || 0;
            const costPrice = parseFloat(item.cost_price) || 0;
            const sellingPrice = parseFloat(item.selling_price) || 0;

            const renderCell = (colId) => {
              switch (colId) {
                case 'serial':
                  return index + 1;
                case 'sku':
                  return <span className="sku-cell fw-medium">{item.public_id || item.sku || 'N/A'}</span>;
                case 'name':
                  return <span className="name-cell fw-bold">{item.name}</span>;
                case 'category':
                  return item.category?.name || item.category_name || 'Uncategorized';
                case 'hsn':
                  return <span className="hsn-cell text-muted">{item.hsn || 'N/A'}</span>;
                case 'gst':
                  return <span className="gst-cell">{item.gst_rate || 0}%</span>;
                case 'cost':
                  return `₹${costPrice.toFixed(2)}`;
                case 'price':
                  return <span className="price-cell text-primary fw-semibold">₹{sellingPrice.toFixed(2)}</span>;
                case 'stock':
                  return (
                    <div className="d-flex flex-column">
                      <span className={`badge ${quantity <= reorderLevel ? 'bg-warning text-dark' : 'bg-info text-dark'}`}>
                        {quantity}
                      </span>
                      {reorderLevel > 0 && (
                        <small className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                          Min: {reorderLevel}
                        </small>
                      )}
                    </div>
                  );
                case 'status':
                  return (
                    <span className={`badge ${item.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                      {item.status ? item.status.replace('_', ' ').toUpperCase() : 'N/A'}
                    </span>
                  );
                case 'actions':
                  return (
                    <div className="actions d-flex gap-2 justify-content-end">
                      <button
                        onClick={() => onEdit(item)}
                        className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                        title="Edit"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        onClick={() => onDelete(item.id)}
                        className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  );
                default:
                  return null;
              }
            };

            return (
              <tr key={item.id} className="align-middle">
                {tableColumns.map(col => (
                  <td key={col.id} className={col.id === 'actions' ? 'text-end' : ''}>
                    {renderCell(col.id)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default InventoryList;